// Inline autocomplete menu for the message composer.
//
// A floating popover anchored to the caret of a textarea. The composer
// owns the open/close decision (via `useAutocomplete`) and the
// suggestion list (via `autocomplete-providers`); this component is
// the visual + keyboard surface only.
//
// Visual spec (chat-design-brief.md):
//   - 280px wide, max-height 280px (≈ 8 rows × 40px = 320px capped).
//   - Anchored to the caret with a 4px offset, flips above when the
//     caret sits within (height + offset) of the viewport bottom.
//   - Row: 40px tall, 8/12px padding, leading 20×20 visual.
//   - Active row: `--chat-surface-raised` bg.
//   - Empty state: `type-body-sm` `--chat-fg-muted`, caller copy.
//
// Keyboard:
//   - ↑/↓ navigate. Wraps at the ends.
//   - Enter selects + closes.
//   - Tab selects + stays (caller passes `keepOpen: true`).
//   - Escape closes.
// All key handling is the caller's job — the menu exposes
// `onKeyDown` so the composer can route keystrokes from the textarea
// here while keeping the textarea focused (the menu never grabs
// focus).

import {
  useCallback,
  useEffect,
  useId,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  forwardRef,
  type CSSProperties,
  type KeyboardEvent,
} from "react";
import { createPortal } from "react-dom";

import { usePortalRoot } from "~/components/EmbedPortalContext";
import { useAuthenticatedMedia } from "~/hooks/useAuthenticatedMedia";
import { MemberKindBadge } from "~/components/MemberKindBadge";
import { getInitials } from "~/lib/initials";
import { getPortalViewportSize, resolveAnchorPosition } from "~/lib/portal-positioning";
import type { Client } from "~/lib/matrix";
import type { Suggestion, SuggestionLeading } from "~/lib/autocomplete-providers";

/** Vertical gap between the menu and the caret, per the brief. */
const ANCHOR_OFFSET = 4;
/** Pinned width per the brief. */
const MENU_WIDTH = 280;
/** Cap height — 8 rows of 40px would be 320, but the brief pins 280
 *  for the menu surface (rows scroll inside the cap). */
const MENU_MAX_HEIGHT = 280;
const EDGE_MARGIN = 8;

export interface AutocompleteMenuRef {
  /** Drive keyboard navigation from the parent textarea. Returns true
   *  when the key was handled (caller should preventDefault). */
  handleKeyDown: (event: KeyboardEvent<HTMLTextAreaElement>) => boolean;
  /** Stable element id of the menu (the listbox). Composer wires
   *  this to the textarea's `aria-controls`. */
  listboxId: string;
}

export interface AutocompleteMenuProps {
  client: Client | null;
  /** Whether the menu is rendered. */
  open: boolean;
  /** Suggestions to render. Empty list shows the empty state row. */
  suggestions: readonly Suggestion[];
  /** Whether a server-side fetch is in flight. Renders the
   *  "Searching…" affordance instead of "No matches" while the
   *  loading is true and the suggestion list is empty. */
  loading?: boolean;
  /**
   * The textarea the menu anchors to. The menu measures the caret
   * position via a hidden mirror element each render so the popover
   * tracks the caret as the user types.
   */
  textarea: HTMLTextAreaElement | null;
  /** Caret index within the textarea — the position the menu anchors
   *  to. The composer sources this from `selectionStart` on every
   *  input/selection change, so the popover follows the caret as the
   *  query grows even when the line wraps. */
  caretIndex: number;
  /**
   * Called when the user picks a suggestion. The composer routes
   * this back through `useAutocomplete.select`.
   */
  onPick: (suggestion: Suggestion, opts: { keepOpen: boolean }) => void;
  /** Called when the user presses Escape inside the menu. */
  onClose: () => void;
  /** Optional initial highlight index (Storybook). Defaults to 0. */
  initialHighlight?: number;
  /** Copy for the empty state row. Defaults to "No matches". */
  emptyStateMessage?: string;
  /**
   * Fires whenever the active-option element id changes — composer
   * wires this to the textarea's `aria-activedescendant` so AT users
   * hear arrow-key navigation. The callback receives `undefined` when
   * the menu has no selectable option (empty state, loading state).
   *
   * Prefer over reading `activeOptionId` off the imperative ref:
   * the ref doesn't trigger a re-render in the composer when
   * arrow-key navigation flips the highlight inside the menu, so
   * AT would see a stale id until the next keystroke.
   */
  onActiveOptionIdChange?: (id: string | undefined) => void;
}

export const AutocompleteMenu = forwardRef<AutocompleteMenuRef, AutocompleteMenuProps>(
  function AutocompleteMenu(
    {
      open,
      client,
      suggestions,
      loading = false,
      textarea,
      caretIndex,
      onPick,
      onClose,
      initialHighlight = 0,
      emptyStateMessage = "No matches",
      onActiveOptionIdChange,
    },
    ref,
  ) {
    const portalRoot = usePortalRoot();
    const baseId = useId();
    const menuId = `${baseId}-autocomplete`;
    const optionId = useCallback(
      (index: number) => `${baseId}-autocomplete-option-${index}`,
      [baseId],
    );
    const [highlight, setHighlight] = useState(initialHighlight);
    const [placement, setPlacement] = useState<{
      top: number;
      left: number;
      position: "fixed" | "absolute";
    } | null>(null);
    // Refs to row elements so ↑/↓ can scroll the active row into view.
    const rowRefs = useRef<Array<HTMLDivElement | null>>([]);
    const menuRef = useRef<HTMLDivElement | null>(null);
    const setRowRef = useCallback(
      (index: number) => (el: HTMLDivElement | null) => {
        rowRefs.current[index] = el;
      },
      [],
    );

    // Reset the highlight whenever the suggestion set changes shape so
    // a stale index doesn't point past the end of a smaller list.
    useEffect(() => {
      setHighlight((h) => {
        if (suggestions.length === 0) return 0;
        if (h >= suggestions.length) return 0;
        return h;
      });
    }, [suggestions]);

    // Scroll-into-view: keep the active row visible inside the
    // capped scroll container. `block: 'nearest'` avoids the jolt of
    // a centred scroll when the row is already near an edge.
    useEffect(() => {
      const row = rowRefs.current[highlight];
      if (!row) return;
      row.scrollIntoView({ block: "nearest" });
    }, [highlight, suggestions]);

    // Position the menu next to the caret. We mirror the textarea
    // contents up to the caret index into a hidden `<div>`, measure
    // the bottom-right of that text, and project against the
    // textarea's screen rect. This is the standard textarea-caret
    // technique — the alternative (`Selection` on contenteditable)
    // doesn't apply because the composer is a native `<textarea>`.
    useLayoutEffect(() => {
      if (!open || !textarea) return;
      const update = () => {
        const rect = textarea.getBoundingClientRect();
        const caret = measureCaretPosition(textarea, caretIndex);
        const surface = getPortalViewportSize(portalRoot);
        const measuredHeight = menuRef.current?.offsetHeight ?? MENU_MAX_HEIGHT;
        const caretBottom = rect.top + caret.top + caret.lineHeight;
        const surfaceBottom = surface.offsetTop + surface.height;
        const flipsUp = surfaceBottom - caretBottom < MENU_MAX_HEIGHT + ANCHOR_OFFSET;
        const viewportTop = flipsUp
          ? rect.top + caret.top - ANCHOR_OFFSET - measuredHeight
          : caretBottom + ANCHOR_OFFSET;
        const clampedTop = Math.max(
          surface.offsetTop + EDGE_MARGIN,
          Math.min(surfaceBottom - measuredHeight - EDGE_MARGIN, viewportTop),
        );
        const clampedLeft = Math.max(
          surface.offsetLeft + EDGE_MARGIN,
          Math.min(
            surface.offsetLeft + surface.width - MENU_WIDTH - EDGE_MARGIN,
            rect.left + caret.left,
          ),
        );
        setPlacement(
          resolveAnchorPosition({
            portalRoot,
            viewportTop: clampedTop,
            viewportLeft: clampedLeft,
          }),
        );
      };
      update();
      window.addEventListener("scroll", update, true);
      window.addEventListener("resize", update);
      return () => {
        window.removeEventListener("scroll", update, true);
        window.removeEventListener("resize", update);
      };
    }, [open, textarea, caretIndex, suggestions.length, loading, portalRoot]);

    // Detach + remove the measurement mirror when the menu unmounts.
    // The mirror is keyed off the textarea node so a remount creates
    // a fresh one — but a long-running session that mounts and
    // unmounts the composer (e.g. embed teardown) should not leave
    // measurement <div>s parked on document.body forever.
    useEffect(() => {
      if (!open) return;
      return () => {
        if (textarea) destroyMirror(textarea);
      };
    }, [open, textarea]);

    const pick = useCallback(
      (index: number, keepOpen: boolean) => {
        const choice = suggestions[index];
        if (!choice) return;
        onPick(choice, { keepOpen });
      },
      [suggestions, onPick],
    );

    const handleKeyDown = useCallback(
      (event: KeyboardEvent<HTMLTextAreaElement>): boolean => {
        if (!open) return false;
        if (event.key === "ArrowDown") {
          event.preventDefault();
          setHighlight((h) => (suggestions.length === 0 ? 0 : (h + 1) % suggestions.length));
          return true;
        }
        if (event.key === "ArrowUp") {
          event.preventDefault();
          setHighlight((h) =>
            suggestions.length === 0 ? 0 : (h - 1 + suggestions.length) % suggestions.length,
          );
          return true;
        }
        if (event.key === "Enter") {
          if (suggestions.length === 0) return false;
          event.preventDefault();
          pick(highlight, false);
          return true;
        }
        if (event.key === "Tab") {
          if (suggestions.length === 0) return false;
          event.preventDefault();
          pick(highlight, true);
          return true;
        }
        if (event.key === "Escape") {
          event.preventDefault();
          onClose();
          return true;
        }
        return false;
      },
      [open, suggestions.length, highlight, pick, onClose],
    );

    const activeOptionId =
      open && suggestions.length > 0 && highlight >= 0 && highlight < suggestions.length
        ? optionId(highlight)
        : undefined;

    // Push the active-option id back to the composer on every change
    // — keeps the textarea's `aria-activedescendant` honest under
    // arrow-key navigation, where the menu's internal state change
    // never re-renders the composer.
    const onActiveChangeRef = useRef(onActiveOptionIdChange);
    useEffect(() => {
      onActiveChangeRef.current = onActiveOptionIdChange;
    }, [onActiveOptionIdChange]);
    useEffect(() => {
      onActiveChangeRef.current?.(activeOptionId);
    }, [activeOptionId]);

    useImperativeHandle(ref, () => ({ handleKeyDown, listboxId: menuId }), [handleKeyDown, menuId]);

    const style = useMemo<CSSProperties>(
      () =>
        placement
          ? {
              position: placement.position,
              top: placement.top,
              left: placement.left,
              width: MENU_WIDTH,
              maxHeight: MENU_MAX_HEIGHT,
              zIndex: 60,
            }
          : { position: "fixed", top: -9999, left: -9999, width: MENU_WIDTH, zIndex: 60 },
      [placement],
    );

    if (!open || !portalRoot) return null;

    const showEmpty = !loading && suggestions.length === 0;
    const showLoading = loading && suggestions.length === 0;

    return createPortal(
      <>
        {/* Status row sits OUTSIDE the listbox: ARIA forbids
            non-`option` descendants in a listbox, and screen readers
            otherwise announce "empty list" instead of the message
            itself. We render it as a polite live region so the
            transient "Searching…" / "No matches" reaches AT users
            without trapping focus. */}
        <div role="status" aria-live="polite" className="chat-visually-hidden" aria-atomic="true">
          {showLoading ? "Searching…" : showEmpty ? emptyStateMessage : ""}
        </div>
        <div
          ref={menuRef}
          id={menuId}
          role="listbox"
          aria-label="Autocomplete suggestions"
          style={{
            ...style,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            background: "var(--chat-surface-overlay)",
            border: "1px solid var(--chat-surface-border)",
            borderRadius: "var(--chat-radius-xl)",
            boxShadow: "var(--chat-shadow-popover)",
          }}
        >
          <div
            style={{
              overflowY: "auto",
              maxHeight: MENU_MAX_HEIGHT,
            }}
          >
            {(showLoading || showEmpty) && (
              <StateRow>{showLoading ? "Searching…" : emptyStateMessage}</StateRow>
            )}
            {suggestions.map((suggestion, index) => (
              <Row
                key={suggestion.id}
                ref={setRowRef(index)}
                id={optionId(index)}
                client={client}
                suggestion={suggestion}
                active={index === highlight}
                onMouseEnter={() => setHighlight(index)}
                onMouseDown={(e) => {
                  // Stop the textarea blurring before the click resolves.
                  e.preventDefault();
                  pick(index, false);
                }}
              />
            ))}
          </div>
        </div>
      </>,
      portalRoot,
    );
  },
);

/**
 * Visible "no matches" / "Searching…" row. Rendered with
 * `aria-disabled` and an `option` role so it is a valid listbox
 * child while still being non-selectable. AT users get the live
 * region (above) for the dynamic announcement.
 */
function StateRow({ children }: { children: React.ReactNode }) {
  return (
    <div
      role="option"
      aria-disabled="true"
      aria-selected="false"
      style={{
        height: 40,
        display: "flex",
        alignItems: "center",
        padding: "0 var(--chat-space-3)",
        color: "var(--chat-fg-muted)",
        fontFamily: "var(--chat-font-sans)",
        fontSize: "var(--chat-type-body-sm-size)",
        lineHeight: "var(--chat-type-body-sm-line)",
        cursor: "default",
      }}
    >
      {children}
    </div>
  );
}

interface RowProps {
  id: string;
  client: Client | null;
  suggestion: Suggestion;
  active: boolean;
  onMouseEnter: () => void;
  onMouseDown: (e: React.MouseEvent) => void;
}

const Row = forwardRef<HTMLDivElement, RowProps>(function Row(
  { id, client, suggestion, active, onMouseEnter, onMouseDown },
  ref,
) {
  return (
    <div
      ref={ref}
      id={id}
      role="option"
      aria-selected={active}
      title={suggestion.mentionRoom ? "@room notifies people, not agents" : undefined}
      onMouseEnter={onMouseEnter}
      onMouseDown={onMouseDown}
      data-active={active || undefined}
      style={{
        height: 40,
        display: "flex",
        alignItems: "center",
        gap: "var(--chat-space-2)",
        padding: "0 var(--chat-space-3)",
        background: active ? "var(--chat-surface-raised)" : "transparent",
        cursor: "pointer",
      }}
    >
      <Leading leading={suggestion.leading} client={client} />
      <div
        style={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        }}
      >
        <span
          style={{
            display: "flex",
            alignItems: "baseline",
            gap: "var(--chat-space-1)",
            minWidth: 0,
          }}
        >
          <span
            style={{
              color: "var(--chat-fg-primary)",
              fontFamily: "var(--chat-font-sans)",
              fontSize: "var(--chat-type-body-md-size)",
              lineHeight: "var(--chat-type-body-md-line)",
              fontWeight: 500,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {suggestion.primary}
          </span>
          {suggestion.mxid && <MemberKindBadge userId={suggestion.mxid} />}
        </span>
        {suggestion.secondary && (
          <span
            style={{
              color: "var(--chat-fg-secondary)",
              fontFamily: "var(--chat-font-sans)",
              fontSize: "var(--chat-type-body-sm-size)",
              lineHeight: "var(--chat-type-body-sm-line)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {suggestion.secondary}
          </span>
        )}
      </div>
    </div>
  );
});

function Leading({ leading, client }: { leading: SuggestionLeading; client: Client | null }) {
  if (leading.kind === "emoji") {
    return (
      <span
        aria-hidden
        style={{
          width: 20,
          height: 20,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 16,
          lineHeight: "16px",
          flexShrink: 0,
        }}
      >
        {leading.native}
      </span>
    );
  }
  if (leading.kind === "glyph") {
    return (
      <span
        aria-hidden
        style={{
          width: 20,
          height: 20,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          color: "var(--chat-fg-secondary)",
          fontFamily: "var(--chat-font-sans)",
          fontSize: 14,
          fontWeight: 500,
          flexShrink: 0,
        }}
      >
        {leading.char}
      </span>
    );
  }
  return <AvatarLeading leading={leading} client={client} />;
}

function AvatarLeading({
  leading,
  client,
}: {
  leading: Extract<SuggestionLeading, { kind: "avatar" }>;
  client: Client | null;
}) {
  const mxcUri = leading.url && leading.url.startsWith("mxc://") ? leading.url : null;
  const media = useAuthenticatedMedia(client, mxcUri, {
    thumbnail: { w: 20, h: 20, method: "crop" },
  });
  const resolved = media.src;

  return (
    <span
      aria-hidden
      style={{
        width: 20,
        height: 20,
        borderRadius: "var(--chat-radius-full)",
        background: "var(--chat-surface-raised)",
        color: "var(--chat-fg-secondary)",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: "var(--chat-font-sans)",
        fontWeight: 500,
        fontSize: 10,
        flexShrink: 0,
        overflow: "hidden",
      }}
    >
      {resolved ? (
        <img src={resolved} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
      ) : (
        getInitials(leading.name)
      )}
    </span>
  );
}

/* ── Caret-position measurement ───────────────────────────────────── */

interface CaretPosition {
  /** Top offset of the caret line within the textarea. */
  top: number;
  /** Left offset of the caret column within the textarea. */
  left: number;
  /** Line height in px so the menu's flip threshold is honest. */
  lineHeight: number;
}

/**
 * Measure the visual position of `index` inside `textarea`. Mirrors
 * the textarea's content into a hidden `<div>` and reads the position
 * of a marker span at `index`. Subtle but proven across browsers.
 *
 * The mirror element is created on demand and reused so a typing
 * burst doesn't churn the DOM. Layout reads (`getBoundingClientRect`)
 * happen once per call. `destroyMirror` removes the parked node when
 * the menu unmounts (see the cleanup effect above).
 */
function measureCaretPosition(textarea: HTMLTextAreaElement, index: number): CaretPosition {
  const mirror = ensureMirror(textarea);
  const text = textarea.value.slice(0, Math.max(0, Math.min(index, textarea.value.length)));
  // Mirror content as text + a marker span. Newlines must survive
  // because the textarea uses `white-space: pre-wrap`.
  mirror.textContent = "";
  mirror.appendChild(document.createTextNode(text));
  const marker = document.createElement("span");
  marker.textContent = "\u200b"; // zero-width space — measurable but invisible
  mirror.appendChild(marker);
  // Use the marker's offset within the mirror (relative to the
  // mirror's padding-box origin). The mirror is positioned over the
  // textarea's padding-box too, so the offsets project directly.
  const mirrorRect = mirror.getBoundingClientRect();
  const markerRect = marker.getBoundingClientRect();
  const taRect = textarea.getBoundingClientRect();
  // Translate from mirror-screen → textarea-local coordinates.
  const top = markerRect.top - mirrorRect.top + (mirrorRect.top - taRect.top) - textarea.scrollTop;
  const left =
    markerRect.left - mirrorRect.left + (mirrorRect.left - taRect.left) - textarea.scrollLeft;
  const lineHeight = parseFloat(getComputedStyle(textarea).lineHeight) || 20;
  return { top, left, lineHeight };
}

const MIRROR_KEY = "__chat_autocomplete_mirror__";

function ensureMirror(textarea: HTMLTextAreaElement): HTMLDivElement {
  const cached = (textarea as unknown as Record<string, HTMLDivElement | undefined>)[MIRROR_KEY];
  const existing = cached && cached.isConnected ? cached : null;
  if (existing) {
    syncMirrorStyles(textarea, existing);
    return existing;
  }
  const mirror = document.createElement("div");
  mirror.setAttribute("aria-hidden", "true");
  mirror.style.position = "absolute";
  mirror.style.visibility = "hidden";
  mirror.style.whiteSpace = "pre-wrap";
  mirror.style.wordWrap = "break-word";
  mirror.style.top = "0";
  mirror.style.left = "0";
  mirror.style.pointerEvents = "none";
  document.body.appendChild(mirror);
  syncMirrorStyles(textarea, mirror);
  (textarea as unknown as Record<string, HTMLDivElement>)[MIRROR_KEY] = mirror;
  return mirror;
}

function destroyMirror(textarea: HTMLTextAreaElement): void {
  const cached = (textarea as unknown as Record<string, HTMLDivElement | undefined>)[MIRROR_KEY];
  if (!cached) return;
  if (cached.parentNode) cached.parentNode.removeChild(cached);
  delete (textarea as unknown as Record<string, HTMLDivElement | undefined>)[MIRROR_KEY];
}

/**
 * Style longhands the mirror needs to wrap text identically to the
 * textarea. We deliberately copy longhands rather than the
 * `padding` / `border` shorthands — `getComputedStyle` returns
 * shorthands as empty strings on some browsers, which would silently
 * mis-position the caret.
 */
const MIRROR_STYLE_LONGHANDS = [
  "fontFamily",
  "fontSize",
  "fontWeight",
  "fontStyle",
  "letterSpacing",
  "lineHeight",
  "textIndent",
  "textTransform",
  "tabSize",
  "boxSizing",
  "paddingTop",
  "paddingRight",
  "paddingBottom",
  "paddingLeft",
  "borderTopWidth",
  "borderRightWidth",
  "borderBottomWidth",
  "borderLeftWidth",
  "borderTopStyle",
  "borderRightStyle",
  "borderBottomStyle",
  "borderLeftStyle",
] as const;

function syncMirrorStyles(textarea: HTMLTextAreaElement, mirror: HTMLDivElement) {
  const cs = getComputedStyle(textarea);
  for (const prop of MIRROR_STYLE_LONGHANDS) {
    mirror.style[prop] = cs[prop];
  }
  const rect = textarea.getBoundingClientRect();
  mirror.style.width = `${rect.width}px`;
  mirror.style.height = "auto";
  // Position the mirror over the textarea so its origin aligns with
  // the textarea's content box. Position is in document coordinates
  // since the mirror is appended to <body>.
  mirror.style.top = `${rect.top + window.scrollY}px`;
  mirror.style.left = `${rect.left + window.scrollX}px`;
}
